// src/features/security/data/security.mock.ts
//
// Static presentation data ONLY. There is no security endpoint wired
// into this monorepo yet — every record below is a placeholder for
// visual layout purposes and must not be read as a real security
// configuration. Consumed only through
// features/security/hooks/useSecurityMock.ts and
// useSecurityWorkspaceMock.ts, so there is a single seam to swap for
// a real data source later without touching any component. Same
// shape/rules as features/commission/data/commission.mock.ts and
// features/users/data/users.mock.ts.
//
// This feature intentionally does not import from any other feature —
// entity references (changedBy, etc.) are carried as plain mock
// strings, same self-contained approach OrderCustomerCard already
// uses for customerName/customerId.

export type SecurityStatus = 'active' | 'disabled' | 'warning';

export interface SecurityModule {
  id: string;
  moduleName: string;
  accessLevel: string;
  status: SecurityStatus;
  lastChangedAt: string;
  changedBy: string;
}

export const securityMock: SecurityModule[] = [
  {
    id: 'sec-3001',
    moduleName: 'احراز هویت دو مرحله‌ای',
    accessLevel: 'سطح ۱ - کامل',
    status: 'active',
    lastChangedAt: '1404/10/02',
    changedBy: 'ادمین ارشد',
  },
  {
    id: 'sec-3002',
    moduleName: 'محدودیت IP پنل ادمین',
    accessLevel: 'سطح ۱ - کامل',
    status: 'warning',
    lastChangedAt: '1404/11/18',
    changedBy: 'ادمین امنیت',
  },
  {
    id: 'sec-3003',
    moduleName: 'سیاست رمز عبور',
    accessLevel: 'سطح ۲ - محدود',
    status: 'active',
    lastChangedAt: '1405/03/05',
    changedBy: 'ادمین ارشد',
  },
  {
    id: 'sec-3004',
    moduleName: 'گزارش‌گیری ورود',
    accessLevel: 'سطح ۲ - محدود',
    status: 'active',
    lastChangedAt: '1404/08/27',
    changedBy: 'ادمین امنیت',
  },
  {
    id: 'sec-3005',
    moduleName: 'دسترسی API خارجی',
    accessLevel: 'سطح ۳ - نظارتی',
    status: 'disabled',
    lastChangedAt: '1404/05/14',
    changedBy: 'ادمین ارشد',
  },
  {
    id: 'sec-3006',
    moduleName: 'کنترل نشست‌های همزمان',
    accessLevel: 'سطح ۲ - محدود',
    status: 'warning',
    lastChangedAt: '1405/04/22',
    changedBy: 'ادمین امنیت',
  },
  {
    id: 'sec-3007',
    moduleName: 'فایروال برنامه وب',
    accessLevel: 'سطح ۱ - کامل',
    status: 'active',
    lastChangedAt: '1404/09/30',
    changedBy: 'ادمین ارشد',
  },
  {
    id: 'sec-3008',
    moduleName: 'رمزنگاری داده‌های حساس',
    accessLevel: 'سطح ۱ - کامل',
    status: 'disabled',
    lastChangedAt: '1403/12/11',
    changedBy: 'ادمین امنیت',
  },
];

// --- Security Workspace Foundation ----------------------------------------
//
// Everything below is additional static mock data for the per-module
// workspace route (/security/:id). Same rule as securityMock above: no
// real endpoint exists yet, this is presentation-only placeholder
// data, and it is only ever read through
// features/security/hooks/useSecurityWorkspaceMock.ts — components
// never import these maps directly. Keyed by SecurityModule['id'] so
// the workspace hook can do a simple lookup; a module with no entry in
// a given map just renders that card's empty state.

export interface SecurityPolicy {
  name: string;
  status: SecurityStatus;
  rules: string[];
}

export interface SecurityPermissionGroup {
  id: string;
  groupName: string;
  accessLevel: string;
  permissions: string[];
}

export interface SecuritySession {
  id: string;
  device: string;
  ip: string;
  lastActivity: string;
}

export type SecurityTimelineEventType = 'policy' | 'access' | 'alert' | 'change';

export interface SecurityTimelineEvent {
  id: string;
  type: SecurityTimelineEventType;
  title: string;
  date: string;
}

export const securityPolicyMock: Record<string, SecurityPolicy> = {
  'sec-3001': {
    name: 'سیاست احراز هویت دو مرحله‌ای',
    status: 'active',
    rules: ['الزامی برای همه ادمین‌ها', 'اعتبار کد: ۳۰ ثانیه', 'حداکثر ۳ تلاش ناموفق'],
  },
  'sec-3002': {
    name: 'سیاست محدودیت IP',
    status: 'warning',
    rules: ['فقط IPهای لیست سفید مجاز است', 'لیست سفید به‌روزرسانی نشده'],
  },
  'sec-3003': {
    name: 'سیاست رمز عبور',
    status: 'active',
    rules: ['حداقل ۱۲ کاراکتر', 'ترکیب حروف بزرگ، کوچک و عدد', 'انقضا هر ۹۰ روز'],
  },
  'sec-3004': {
    name: 'سیاست گزارش‌گیری ورود',
    status: 'active',
    rules: ['ثبت تمام تلاش‌های ورود', 'نگهداری گزارش‌ها به مدت ۱۸۰ روز'],
  },
  'sec-3005': {
    name: 'سیاست دسترسی API خارجی',
    status: 'disabled',
    rules: ['غیرفعال تا اطلاع ثانوی'],
  },
  'sec-3006': {
    name: 'سیاست نشست‌های همزمان',
    status: 'warning',
    rules: ['حداکثر ۲ نشست فعال برای هر کاربر', 'برخی حساب‌ها بیش از حد مجاز نشست دارند'],
  },
  'sec-3007': {
    name: 'سیاست فایروال برنامه وب',
    status: 'active',
    rules: ['فیلتر درخواست‌های مشکوک', 'مسدودسازی خودکار IP پرتکرار'],
  },
  'sec-3008': {
    name: 'سیاست رمزنگاری داده‌های حساس',
    status: 'disabled',
    rules: ['در انتظار پیکربندی کلید رمزنگاری جدید'],
  },
};

export const securityPermissionsMock: Record<string, SecurityPermissionGroup[]> = {
  'sec-3001': [
    { id: 'perm-1', groupName: 'ادمین ارشد', accessLevel: 'کامل', permissions: ['فعال‌سازی اجباری', 'مدیریت دستگاه‌های مورد اعتماد'] },
  ],
  'sec-3002': [
    { id: 'perm-2', groupName: 'ادمین امنیت', accessLevel: 'محدود', permissions: ['ویرایش لیست سفید IP'] },
  ],
  'sec-3003': [
    { id: 'perm-3', groupName: 'ادمین ارشد', accessLevel: 'کامل', permissions: ['تغییر سیاست رمز عبور', 'بازنشانی رمز کاربران'] },
    { id: 'perm-4', groupName: 'پشتیبانی', accessLevel: 'محدود', permissions: ['بازنشانی رمز کاربران'] },
  ],
  'sec-3004': [
    { id: 'perm-5', groupName: 'ادمین امنیت', accessLevel: 'نظارتی', permissions: ['مشاهده گزارش‌های ورود'] },
  ],
  'sec-3005': [],
  'sec-3006': [
    { id: 'perm-6', groupName: 'ادمین امنیت', accessLevel: 'محدود', permissions: ['پایان‌دادن به نشست‌ها'] },
  ],
  'sec-3007': [
    { id: 'perm-7', groupName: 'ادمین ارشد', accessLevel: 'کامل', permissions: ['ویرایش قوانین فایروال', 'مدیریت IPهای مسدود'] },
  ],
  'sec-3008': [],
};

export const securitySessionsMock: Record<string, SecuritySession[]> = {
  'sec-3001': [
    { id: 'sess-1', device: 'ویندوز - Chrome', ip: '185.23.14.90', lastActivity: '۱۰ دقیقه پیش' },
    { id: 'sess-2', device: 'اندروید - Vania App', ip: '5.211.34.12', lastActivity: '۲ ساعت پیش' },
  ],
  'sec-3002': [{ id: 'sess-3', device: 'مک - Safari', ip: '78.39.201.4', lastActivity: '۱ ساعت پیش' }],
  'sec-3003': [],
  'sec-3004': [{ id: 'sess-4', device: 'ویندوز - Edge', ip: '46.100.12.77', lastActivity: '۵ ساعت پیش' }],
  'sec-3005': [],
  'sec-3006': [
    { id: 'sess-5', device: 'ویندوز - Chrome', ip: '185.23.14.90', lastActivity: '۳۰ دقیقه پیش' },
    { id: 'sess-6', device: 'iOS - Vania App', ip: '95.81.44.19', lastActivity: '۱ روز پیش' },
    { id: 'sess-7', device: 'لینوکس - Firefox', ip: '31.7.68.201', lastActivity: '۲ روز پیش' },
  ],
  'sec-3007': [{ id: 'sess-8', device: 'ویندوز - Chrome', ip: '185.23.14.90', lastActivity: '۱۵ دقیقه پیش' }],
  'sec-3008': [],
};

export const securityTimelineMock: Record<string, SecurityTimelineEvent[]> = {
  'sec-3001': [
    { id: 'stl-1', type: 'change', title: 'فعال‌سازی احراز هویت دو مرحله‌ای', date: '1404/10/02' },
    { id: 'stl-2', type: 'access', title: 'ورود موفق ادمین ارشد', date: '۱۰ دقیقه پیش' },
  ],
  'sec-3002': [
    { id: 'stl-3', type: 'alert', title: 'لیست سفید IP به‌روزرسانی نشده', date: '1404/11/18' },
  ],
  'sec-3003': [
    { id: 'stl-4', type: 'policy', title: 'به‌روزرسانی سیاست رمز عبور', date: '1405/03/05' },
  ],
  'sec-3004': [
    { id: 'stl-5', type: 'change', title: 'فعال‌سازی گزارش‌گیری ورود', date: '1404/08/27' },
  ],
  'sec-3005': [
    { id: 'stl-6', type: 'change', title: 'غیرفعال‌سازی دسترسی API خارجی', date: '1404/05/14' },
  ],
  'sec-3006': [
    { id: 'stl-7', type: 'alert', title: 'شناسایی نشست‌های مازاد بر حد مجاز', date: '1405/04/22' },
  ],
  'sec-3007': [
    { id: 'stl-8', type: 'policy', title: 'به‌روزرسانی قوانین فایروال', date: '1404/09/30' },
  ],
  'sec-3008': [
    { id: 'stl-9', type: 'change', title: 'غیرفعال‌سازی موقت رمزنگاری', date: '1403/12/11' },
  ],
};
